import { useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import {
  StoplossClaimPreviewDialog,
  type IncomingClaimLine,
} from "@/resources/stoploss-claims/components/StoplossClaimPreviewDialog";
import { getResourceIcon } from "@/utils/icons";
import { AlertType, ClaimantProjection, ResourceName, ValidationError } from "@/types";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ValidationErrorsCell } from "./ReviewChips";

interface ForecastChipProps {
  /** The projection for this record; undefined if projection hasn't loaded or record didn't map. */
  projection: ClaimantProjection | undefined;
  /**
   * This document's claim lines for the claimant that will be inserted on
   * approve (not already on file) — shown in the open-claim preview as the
   * incoming rows (walkthrough 2026-10-19).
   */
  incomingLines?: IncomingClaimLine[];
  /** Existing validation/transformation errors — take precedence over forecast when present. */
  errors: ValidationError[];
  /** True while the projection endpoint is fetching. */
  isLoading: boolean;
}

/**
 * Semantic chip summarizing the forecasted outcome of approving the document
 * for a single claimant row. Falls back to ValidationErrorsCell only for
 * blocking (error-severity) issues. Warnings are advisory and surfaced
 * elsewhere — they do not belong in the "What happens" column.
 *
 * One outcome per row, in the redesign's voice (artboard 2a):
 *   - "Creates SLC"               — will_create_sl_claim (navy pill, SLC icon)
 *   - "Adds to open SLC"          — will_update_sl_claim_id (outlined pill, SLC icon;
 *                                   opens StoplossClaimPreviewDialog in place — the
 *                                   aegle_id is too long to read on a chip)
 *   - "Creates claimant"          — match_status=new, no SL action (bold text);
 *                                   also under is_pending_family_link, with the
 *                                   grid's "No stop-loss claim until linked" sub-line
 *   - "Matched · adds Member ID"  — match_status=matched_backfill, no SL action
 *   - "Updates YTD" / "No updates" — matched, quiet text
 *
 * "Computing…" / "—" / ValidationErrorsCell handle loading / missing / blocking
 * states in the wrapper above. When match_status=matched_backfill coexists with
 * a financial action, the action chip's tooltip is extended to mention the
 * soft-merge so reviewers still see it.
 */
export const ForecastChip = ({ projection, errors, isLoading, incomingLines }: ForecastChipProps) => {
  const blockingErrors = errors.filter((e) => e.severity === AlertType.ERROR);

  if (blockingErrors.length > 0) {
    return <ValidationErrorsCell errors={blockingErrors} />;
  }

  if (isLoading) {
    return (
      <Typography variant="body2" color="text.disabled">
        Computing…
      </Typography>
    );
  }

  if (!projection) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  return <ForecastChipVariant projection={projection} incomingLines={incomingLines} />;
};

const BACKFILL_TOOLTIP_SUFFIX =
  " Also matched an existing claimant on this client; approving will write the incoming Member ID onto the row — verify the soft-merge before approving.";

/** The app's stop-loss claim icon (src/utils/icons.ts), sized for the pill. */
const StoplossClaimIcon = getResourceIcon(ResourceName.STOPLOSS_CLAIMS);
const SlcGlyph = () => <StoplossClaimIcon sx={{ fontSize: 13, flexShrink: 0 }} aria-hidden />;

/**
 * One outcome per row, in the design's voice (artboard 2a, 2026-10-15):
 * stop-loss outcomes are navy pills with the app's stop-loss claim icon
 * (filled = creates, outlined = adds to an open claim); creating a claimant is bold text; a YTD
 * change or no change is quiet text. The "New" status chip is gone — "Creates
 * claimant" says it, and a new claimant that also breaches shows the pill
 * alone, as in the mockup.
 */
const ForecastChipVariant = ({
  projection,
  incomingLines,
}: {
  projection: ClaimantProjection;
  incomingLines?: IncomingClaimLine[];
}) => {
  const { theme } = useAppTheme();
  const [previewOpen, setPreviewOpen] = useState(false);

  const isNew = projection.match_status === "new";
  const isBackfill = projection.match_status === "matched_backfill";
  const willCreate = projection.will_create_sl_claim;
  const willUpdate = !!projection.will_update_sl_claim_id;
  const isPendingFamilyLink = projection.is_pending_family_link;

  const navy = theme.palette.secondary.main;
  // The pill is a plain box, as the mockup draws it (inline-flex, 24px, 12px
  // radius, 12/600). MUI's Chip icon slot restyles the glyph (margins, colour,
  // a background from the theme's chip overrides) and the shield came out as a
  // blob — walkthrough 2026-10-15.
  // The pill is the !4938 span, visually unchanged; `onClick` adds a button
  // role so "Adds to open SLC" opens the claim preview in place (walkthrough
  // 2026-10-15: the aegle_id is too long to read on a chip, and a new tab
  // took the reviewer out of the review). A real <button> changed the pill's
  // look (browser defaults), so the role is on the span instead.
  const pill = (label: string, filled: boolean, onClick?: () => void) => (
    <Box
      component="span"
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={
        onClick
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      onKeyDown={
        onClick
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        height: 24,
        px: 1,
        borderRadius: "12px",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
        bgcolor: filled ? navy : "transparent",
        color: filled ? theme.palette.getContrastText(navy) : navy,
        border: filled ? "none" : `1px solid ${navy}`,
        textDecoration: "none",
        maxWidth: "100%",
        ...(onClick
          ? {
              cursor: "pointer",
              "&:hover": { bgcolor: `${navy}14` },
              "&:focus-visible": { outline: `2px solid ${navy}`, outlineOffset: 2 },
            }
          : {}),
      }}
    >
      <SlcGlyph />
      {label}
    </Box>
  );
  const text = (label: string, opts: { bold?: boolean; tone?: string } = {}) => (
    <Typography
      component="span"
      variant="body2"
      sx={{ fontWeight: opts.bold ? 600 : 400, color: opts.tone ?? "text.secondary", whiteSpace: "nowrap" }}
    >
      {label}
    </Typography>
  );
  const withTip = (title: string, node: React.ReactElement) => (
    <Tooltip title={title} placement="top" arrow>
      <span style={{ display: "inline-flex" }}>{node}</span>
    </Tooltip>
  );

  // Pending family unit — the load pipeline holds the claimant (no subscriber
  // link), so no stop-loss claim is created however large the spend. Checked
  // first so the forecast reflects the hold instead of promising a claim.
  // The grid's sub-line carries "No stop-loss claim until linked".
  if (isPendingFamilyLink) {
    return withTip(
      "This claimant is on a family-deductible policy but not tied to a family unit. Approving will create the claimant record, but no stop-loss claim will be created until a family unit is formed on the claimant page.",
      text(isNew ? "Creates claimant" : "Updates claimant", { bold: isNew, tone: "warning.dark" })
    );
  }

  if (willCreate) {
    const baseTitle = isNew
      ? "New claimant; approving will create a stop-loss claim."
      : "Approving will create a stop-loss claim.";
    return withTip(isBackfill ? baseTitle + BACKFILL_TOOLTIP_SUFFIX : baseTitle, pill("Creates SLC", true));
  }

  if (willUpdate) {
    // No aegle_id on the pill or its tooltip (2026-10-19): the preview dialog
    // identifies the claim, and the id is on the full page.
    const baseTitle =
      "Approving adds this document's spend to this claimant's open stop-loss claim. Click to preview the claim.";
    return (
      <>
        {withTip(
          isBackfill ? baseTitle + BACKFILL_TOOLTIP_SUFFIX : baseTitle,
          pill("Adds to open SLC", false, () => setPreviewOpen(true))
        )}
        <StoplossClaimPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          stoplossClaimId={projection.will_update_sl_claim_id ? String(projection.will_update_sl_claim_id) : null}
          claimantName={projection.claimant_name}
          incomingLines={incomingLines}
          onApprove={{
            adds: projection.ytd_delta ?? projection.ytd_after - projection.ytd_before,
            deductible: projection.applicable_deductible ?? 0,
          }}
        />
      </>
    );
  }

  // Matched-with-backfill — soft-merge state without a financial action. The
  // system matched a name to an existing claimant whose member_id was NULL;
  // approving writes the incoming member_id onto that row. Surfaced so the
  // reviewer can verify (a false positive merges two real people).
  if (isBackfill) {
    return withTip(
      "Matched an existing claimant on this client. Approving will write the incoming Member ID onto the row — verify the soft-merge is correct before approving.",
      text("Matched · adds Member ID", { bold: true, tone: "text.primary" })
    );
  }

  if (isNew) {
    return withTip(
      "New claimant; approving will create the record.",
      text("Creates claimant", { bold: true, tone: "text.primary" })
    );
  }

  const ytdDelta =
    typeof projection.ytd_delta === "number" ? projection.ytd_delta : projection.ytd_after - projection.ytd_before;
  if (ytdDelta === 0) {
    return withTip(
      "Existing claimant with no new items in this document — YTD is unchanged and no stop-loss action will be triggered.",
      text("No updates")
    );
  }
  return withTip("Existing claimant; YTD updates but no stop-loss action triggered.", text("Updates YTD"));
};
